import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { useMascara } from '../../valores-ocultos/contexto';

/**
 * A faixa preta do rodapé: as assembleias dos próximos 7 dias que têm cota do
 * escopo em jogo.
 *
 * Fonte: servopa_vendas_with_details, que já carrega a PRÓXIMA assembleia de
 * cada venda (next_assembleia_date, via LATERAL na migration 74) e o estado do
 * lance do mês (next_lance_status). "Sem lance" segue a régua da
 * seller_action_queue (migration 89): status nulo, failed ou cancelled.
 *
 * A faixa some quando nenhuma cota do escopo decide em 7 dias — semana sem
 * assembleia não é aviso, é ausência.
 */

const DIAS_JANELA = 7;
const MAX_CARDS = 4;

type Grupo = {
  data: string; // YYYY-MM-DD
  grupo: number;
  cotas: number;
  semLance: number;
};

const SEM_LANCE = new Set(['failed', 'cancelled']);

export const FaixaAssembleias = ({
  effectiveIds,
  navigate,
}: {
  /** sales_person_ids do escopo; null = sem filtro (super admin em "Todos"). */
  effectiveIds: number[] | null;
  navigate: (to: string) => void;
}) => {
  const m = useMascara();
  const [grupos, setGrupos] = useState<Grupo[] | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const hoje = new Date();
      const ateISO = new Date(hoje.getTime() + DIAS_JANELA * 86_400_000)
        .toISOString()
        .slice(0, 10);
      let query = supabase
        .from('servopa_vendas_with_details')
        .select('group_number, next_assembleia_date, next_lance_status')
        .eq('status', 'confirmed')
        .eq('contemplado', false)
        .gte('next_assembleia_date', hoje.toISOString().slice(0, 10))
        .lte('next_assembleia_date', ateISO);
      if (effectiveIds && effectiveIds.length > 0) {
        query = query.in('sales_person_id', effectiveIds);
      }
      const { data } = await query;
      if (!ativo) return;

      const porChave = new Map<string, Grupo>();
      for (const r of (data ?? []) as {
        group_number: number | null;
        next_assembleia_date: string | null;
        next_lance_status: string | null;
      }[]) {
        if (!r.next_assembleia_date || r.group_number == null) continue;
        const chave = `${r.next_assembleia_date}|${r.group_number}`;
        const g = porChave.get(chave) ?? {
          data: r.next_assembleia_date,
          grupo: r.group_number,
          cotas: 0,
          semLance: 0,
        };
        g.cotas += 1;
        if (r.next_lance_status == null || SEM_LANCE.has(r.next_lance_status)) g.semLance += 1;
        porChave.set(chave, g);
      }
      setGrupos(
        [...porChave.values()].sort(
          (a, b) => a.data.localeCompare(b.data) || a.grupo - b.grupo,
        ),
      );
    })();
    return () => {
      ativo = false;
    };
  }, [effectiveIds]);

  if (!grupos || grupos.length === 0) return null;

  const totalCotas = grupos.reduce((s, g) => s + g.cotas, 0);
  const nGrupos = new Set(grupos.map((g) => g.grupo)).size;
  const fmtData = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

  return (
    <div className="flex flex-wrap items-center gap-7 rounded-lg bg-[#0E1619] px-6 py-5 text-white md:col-span-2 xl:col-span-4 xl:flex-nowrap">
      <div className="shrink-0">
        <p className="text-[12.5px] font-semibold text-[rgba(255,255,255,0.6)]">
          Assembleias em {DIAS_JANELA} dias
        </p>
        <p className="mt-1.5 text-[26px] font-bold leading-none tracking-[-0.03em]">
          {m(nGrupos)} {nGrupos === 1 ? 'grupo' : 'grupos'} · {m(totalCotas)}{' '}
          {totalCotas === 1 ? 'cota' : 'cotas'}
        </p>
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap gap-2.5">
        {grupos.slice(0, MAX_CARDS).map((g) => (
          <div
            key={`${g.data}-${g.grupo}`}
            className="min-w-[150px] flex-1 rounded-xl bg-white/[0.07] px-3.5 py-2.5"
          >
            <p className="text-[11px] text-white/55">
              {fmtData(g.data)} · Grupo {g.grupo}
            </p>
            <p className="mt-1 text-[13px] font-semibold">
              {g.semLance > 0
                ? `${m(g.semLance)} ${g.semLance === 1 ? 'cota sem lance' : 'cotas sem lance'}`
                : `${m(g.cotas)} ${g.cotas === 1 ? 'cota com lance' : 'cotas com lance'}`}
            </p>
          </div>
        ))}
        {grupos.length > MAX_CARDS && (
          <div className="flex items-center px-2 text-xs text-white/55">
            {m(`+${grupos.length - MAX_CARDS}`)} assembleias
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => navigate('/lances')}
        className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg bg-secondary px-4 text-[13px] font-semibold text-[#0E1619] hover:opacity-90"
      >
        Abrir Lances
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
